import {
    Box,
    CardActions,
    CardContent,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import React from "react";
import dayjs from "dayjs";
import { SurveyorResponseLabel } from "../surveyor_response_list/surveyor_response_list";
import { DownloadOutlined } from "@mui/icons-material";
import { purple } from "../../../../theme/colors";
import {
    TableCell,
    TableEmptyState,
    TableFooterPagination,
    TableHeaderRow,
    TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

interface DataGridInterface {
    labels: SurveyorResponseLabel[];
    data: {
        adminEmailId: string,
        adminId: number,
        adminName: string,
        choiceName: string,
        id: number,
        processedAt: string,
        status: string,
        recordCount: number;
        remark: {
            details: [],
            message: string,
            query: string,
        }
    }[];
    count: number;
    page: number;
    setPage: (val: number) => void;
    pageSize: number;
    setPageSize: (val: number) => void;
    downloadResponse: (taskId: number) => void
}

export default function DataGridDownloads({
    labels,
    data,
    page,
    setPage,
    pageSize,
    setPageSize,
    count,
    downloadResponse
}: DataGridInterface) {

    const startRange = page * pageSize + 1;
    const endRange = Math.min((page + 1) * pageSize, count);
    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage);
    };

    const summaryText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;

    function statusColorCodeBasedOnStatus({ status }: { status: string }) {
        if (status === "Pending") {
            return "#91278F";
        } else if (status === "Completed") {
            return "#1E6446";
        } else {
            return "#D14C2E";
        }
    }

    return (
        <Box display="flex" flex={1} flexDirection="column" height="100%" width="100%">
            <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                {data.length === 0 ? (
                    <TableEmptyState message="No downloads found" hint="Try adjusting your search or filters." />
                ) : (
                    <Box mt={1}>
                        <TableHeaderRow labels={labels.map((item) => ({ label: item.label }))} align="left" bordered />
                        {data.map((item, index) => {
                            const formattedDate = dayjs(item?.processedAt)?.format?.("DD MMM YYYY");
                            return (
                                <TableRow key={item.id} bordered>
                                    <TableCell label="SL. No" bordered>{page * pageSize + index + 1}</TableCell>
                                    <TableCell label="Survey Name" bordered>{item?.choiceName}</TableCell>
                                    <TableCell label="Status" bordered>
                                        <Typography fontWeight={600} fontSize={14} color={statusColorCodeBasedOnStatus({ status: item?.status })}>
                                            {item?.status}
                                        </Typography>
                                    </TableCell>
                                    <TableCell label="Remark" bordered>{item?.remark?.message ? item?.remark?.message : "-"}</TableCell>
                                    <TableCell label="Processed By" bordered>{item?.adminName}</TableCell>
                                    <TableCell label="Processed At" bordered>{formattedDate}</TableCell>
                                    <TableCell label="Record Count" bordered>{item?.recordCount}</TableCell>
                                    <TableCell label="Action" bordered isLast>
                                        <Tooltip title="Download">
                                            <span>
                                                <IconButton
                                                    disabled={item?.status !== "Completed"}
                                                    onClick={() => downloadResponse(item?.id)}
                                                    sx={{ color: purple }}
                                                >
                                                    <DownloadOutlined />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </Box>
                )}
            </CardContent>
            {data.length > 0 && (
                <CardActions sx={{ px: 3, pb: 3 }}>
                    <Box width="100%">
                        <TableFooterPagination
                            count={count}
                            page={page}
                            onPageChange={handleChangePage}
                            summaryText={summaryText}
                            rowsPerPage={pageSize}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            onRowsPerPageChange={(newSize) => {
                                setPageSize(newSize);
                                setPage(0);
                            }}
                        />
                    </Box>
                </CardActions>
            )}
        </Box>
    );
}
